import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  declineFullAssessment,
  upgradeToFullAssessment,
} from "../api/client";
import LiteReportButton from "../components/LiteReportButton";
import { upgradeOfferTitle, isFiveXType, continueFullTitle } from "../utils/participantAssessmentType";

function BrandLockup({ branding }) {
  const logo = branding?.logo_url;
  const name = branding?.product_display_name || branding?.company_name;
  if (!logo && !name) return null;
  return (
    <div className="flex items-center justify-center gap-2 mb-4">
      {logo ? <img src={logo} alt="" className="h-8 w-auto max-w-[140px] object-contain" /> : null}
      {name && !logo ? (
        <p className="text-sm font-semibold text-brand">{name}</p>
      ) : null}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      className="w-8 h-8 text-emerald-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      className="w-5 h-5 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg
      className="w-6 h-6 text-brand"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

export default function UpgradeOfferPage({
  surveyToken,
  fullSurveyToken = null,
  offerLiteReport = true,
  remainingQuestionCount = null,
  assessmentType,
  branding = null,
  onContinueFull,
  onLater,
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const remaining =
    remainingQuestionCount != null && Number(remainingQuestionCount) >= 0
      ? Number(remainingQuestionCount)
      : null;
  const remainingMinutes =
    remaining != null
      ? Math.max(1, Math.round((remaining * 40) / 60))
      : null;
  const fiveX = isFiveXType(assessmentType);
  const fullTitle = continueFullTitle(assessmentType);

  const handleYes = async () => {
    setBusy(true);
    try {
      if (onContinueFull) {
        await onContinueFull();
        return;
      }
      if (fullSurveyToken) {
        navigate(`/a/${fullSurveyToken}`, { replace: true });
        return;
      }
      const res = await upgradeToFullAssessment(surveyToken);
      const newToken = res.data?.full_survey_token;
      if (!newToken) {
        toast.error("Could not start full assessment.");
        return;
      }
      navigate(`/a/${newToken}`, { replace: true });
    } catch (e) {
      toast.error(e.message || "Could not start full assessment");
    } finally {
      setBusy(false);
    }
  };

  const handleLater = async () => {
    setBusy(true);
    try {
      await declineFullAssessment(surveyToken);
      onLater?.();
    } catch (e) {
      toast.error(e.message || "Could not save your choice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-10 sm:py-14 bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="max-w-2xl w-full">
        <BrandLockup branding={branding} />
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 mb-4">
            <CheckIcon />
          </div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700 mb-2">
            {upgradeOfferTitle(assessmentType)}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand">
            Great work — what would you like to do next?
          </h1>
        </div>

        <div className="flex flex-col gap-5">
          {/* Primary: Continue full assessment */}
          <article className="relative rounded-2xl border-2 border-brand bg-gradient-to-br from-brand/5 via-white to-brand/[0.07] shadow-lg shadow-brand/10 p-6 sm:p-8">
            <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-brand px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
              Recommended
            </span>
            <h2 className="text-xl sm:text-2xl font-bold text-brand mt-2 mb-3">
              Continue with {fullTitle}
            </h2>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
              {remaining != null ? (
                <>
                  Continue with{" "}
                  <strong>
                    {remaining} remaining question{remaining === 1 ? "" : "s"}
                  </strong>
                  {remainingMinutes != null ? (
                    <> (~{remainingMinutes} min)</>
                  ) : null}
                  {fiveX
                    ? " across the remaining pillars and financial section — the most complete picture for your leadership team."
                    : " for a deeper view across the remaining areas — the most complete picture for your team."}
                </>
              ) : fiveX ? (
                <>
                  Go deeper across all pillars with additional questions and a
                  financial section — the most complete picture for your
                  leadership team.
                </>
              ) : (
                <>
                  Go deeper with the remaining questions for a fuller picture of
                  how your team is performing.
                </>
              )}
            </p>
            <ul className="text-sm text-gray-600 space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <span className="text-brand font-bold mt-0.5">•</span>
                {remaining != null
                  ? `${remaining} additional question${
                      remaining === 1 ? "" : "s"
                    } for richer insight`
                  : fiveX
                    ? "More questions per pillar for richer insight"
                    : "More questions for richer insight"}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand font-bold mt-0.5">•</span>
                Pick up right away — your LITE answers are saved
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand font-bold mt-0.5">•</span>
                Same invitation link if you need to pause and return
              </li>
            </ul>
            <button
              type="button"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-8 py-3.5 text-base font-semibold text-white shadow-md hover:opacity-90 disabled:opacity-50 transition-opacity"
              onClick={handleYes}
              disabled={busy}
            >
              {busy ? "Starting…" : "Continue with full assessment"}
              {!busy && <ArrowIcon />}
            </button>
          </article>

          {/* Secondary: Check survey results */}
          {offerLiteReport ? (
          <article className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50/80 p-6 sm:p-7 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-amber-100 shadow-sm">
                <ChartIcon />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-1">
                  Your LITE results are ready
                </p>
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  Check survey results here
                </h2>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  View your LITE assessment report now for early insights on
                  alignment and priorities before you continue.
                </p>
                <LiteReportButton
                  surveyToken={surveyToken}
                  label="View LITE assessment report"
                  className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl border-2 border-amber-400 bg-white px-6 py-3 text-sm font-semibold text-amber-900 hover:bg-amber-50 shadow-sm transition-colors"
                />
              </div>
            </div>
          </article>
          ) : null}
        </div>

        <footer className="mt-8 text-center">
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-brand underline underline-offset-2 disabled:opacity-50"
            onClick={handleLater}
            disabled={busy}
          >
            I&apos;ll continue the full assessment later
          </button>
        </footer>
      </div>
    </section>
  );
}
